import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Box, Column, Row, Tooltip, Typography } from '@tempmee/react';
import { colors } from '@tempmee/tailwind';
import { Divider } from '~/commonComponents';
import { JobPostingStatus, JobPostingStatusMapper } from '~/domain/PermanentJobs';
import { useJobProfessionDisplay } from '~/hooks';
import { WarningSolidIcon } from '~/images/icons';
import UserIcon from '~/images/user-icon.svg';
import { Badge } from '../../../components';
import { TJobCardProps } from './Types';

export const JobCard = ({
  candidateCount,
  closedAt,
  id,
  office,
  onsiteInterviewsCount,
  professionType,
  publishedAt,
  status,
  subProfession,
  title,
}: TJobCardProps) => {
  const navigate = useNavigate();

  const fmtTimestamp = useMemo(() => {
    if (status === JobPostingStatus.Closed) {
      return `Closed on ${closedAt}`;
    }

    return `Posted on ${publishedAt}`;
  }, [closedAt, publishedAt, status]);

  const hasNoInterviewSlots = onsiteInterviewsCount === 0;

  const isActiveJob = status === JobPostingStatus.Active;
  const isSuspendedJob = status === JobPostingStatus.Suspended;

  const isShowInterviewCounter =
    isActiveJob && onsiteInterviewsCount !== undefined && onsiteInterviewsCount <= 3;
  const totalCandidates = candidateCount ?? 0;

  const professionTypeDisplay = useJobProfessionDisplay({
    profession: professionType,
    subProfession,
  });

  return (
    <Box
      className="grid items-center cursor-pointer shadow-xs hover__shadow-sm bg-shades-0 rounded-field"
      data-cy="active-job-card"
      key={id}
      py={3}
      style={{ gridTemplateColumns: '1fr 20% 20%' }}
      onClick={() => navigate(`/permanent-jobs/job-overview/${id}`)}
    >
      <Column gap={2} style={{ marginLeft: 36 }}>
        <Row align="center" gap={2}>
          <Typography as="span" bold style={{ textDecoration: 'underline' }}>
            {title || 'UNTITLED'}
          </Typography>
          {isSuspendedJob && (
            <Tooltip
              content="Awaiting Payment"
              placement="bottom"
            >
              <WarningSolidIcon color={colors.warning[500]} size={18} />
            </Tooltip>
          )}
        </Row>
        <Typography as="span">{office?.name}</Typography>
        <Row align="center" gap={3}>
          <Typography as="span">{professionTypeDisplay || 'No Professional Type Selected'}</Typography>
          <Divider width={1} height={12} marginVertical={0} />
          <Typography italic color={colors.neutral[400]} style={{ fontSize: 14 }}>
            {fmtTimestamp}
          </Typography>
          {isShowInterviewCounter && (
            <Badge
              label={`${onsiteInterviewsCount} INTERVIEWS LEFT`}
              variant={hasNoInterviewSlots ? 'error' : 'warning'}
            />
          )}
        </Row>
      </Column>
      <Row align="center" justify="center" gap={1}>
        <img alt="user-icon" src={UserIcon} height={24} width={24} />
        <Typography as="span" bold color={colors.secondary[500]} size="heading_4">
          {totalCandidates}
        </Typography>
      </Row>
      <Row align="center" gap={2}>
        <Box
          height="10px"
          width="10px"
          className="rounded-full"
          style={{ backgroundColor: JobPostingStatusMapper.getColor(status) }}
        />
        <Typography as="span">{JobPostingStatusMapper.toHumanReadable(status)}</Typography>
      </Row>
    </Box>
  );
};
